"use client";

import React, { useRef, useState, useEffect } from "react";

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

import OpenSeadragon from "openseadragon";
import { DownloadIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { HomeIcon, MinusIcon, PlusIcon } from "@heroicons/react/20/solid";

import debounce from "lodash/debounce";

export const ImgPanZoom = ({
  className,
  src,
  // dzi = false,
  // fadeIn = true,
  // hideSpinner = false,
  showNavigationControl = false,
  showNavigator = false,
  zoomPerClick = 2.0,
  zoomPerScroll = 1.2,
  animationTime = 0.7,
  navPrevNextWrap = false,
  showRotationControl = false,
  minZoomImageRatio = 1,
  maxZoomPixelRatio = 5,
  constrainDuringPan = true,
  visibilityRatio = 1,
  defaultZoomLevel = undefined,
  project,
  // tiledImage,
  blurs,
  viewportMargins = {},
  bgClassName,
}: OpenSeadragon.Options & {
  src: string;
  dzi?: boolean;
  fadeIn?: boolean;
  hideSpinner?: boolean;
  project?: string;
  tiledImage?: string;
  bgClassName?: string;
  blurs?: {
    height: number;
    width: number;
    x: number;
    y: number;
  }[];
  className?: string;
}) => {
  const router = useRouter();
  const viewerRef = useRef(null);
  const [viewer, setViewer] = useState<OpenSeadragon.Viewer | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const viewerWrapperRef = useRef<HTMLDivElement>(null);
  const [init, setInit] = useState(false);
  const [grabbing, setGrabbing] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(1);
  const [tiledImage, setTiledImage] = useState<
    OpenSeadragon.TiledImage & { id: string }
  >();
  const [lastBlursSet, setLastBlursSet] = useState("");
  const [isMounted, setIsMounted] = useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  function resetZoom() {
    if (!viewer) return;
    viewer.viewport.goHome();
  }

  function zoomIn() {
    if (!viewer) return;
    // TODO: Replace with native openseadragon zoomIn
    var currentZoom = viewer.viewport.getZoom();
    var maxZoom = viewer.viewport.getMaxZoom();
    var zoomTo = currentZoom + 0.7;
    if (zoomTo < maxZoom) {
      viewer.viewport.zoomTo(zoomTo);
    }
  }

  // Zoom out
  function zoomOut() {
    if (!viewer) return;
    // TODO: Replace with openseadragon native zoomOut
    var currentZoom = viewer.viewport.getZoom();
    var minZoom = viewer.viewport.getMinZoom();
    var zoomTo = currentZoom - 0.7;
    if (zoomTo > minZoom) {
      viewer.viewport.zoomTo(zoomTo);
    } else {
      if (minZoom != currentZoom) {
        resetZoom();
      }
    }
  }

  // Init OpenSeadragon
  useEffect(() => {
    if (
      !viewerRef?.current ||
      viewer ||
      init ||
      // window.openSeadragonInit ||
      !isMounted
    ) {
      return;
    }

    // window.openSeadragonInit = true;
    setInit(true);
    const viewerInstance = OpenSeadragon({
      element: viewerRef.current,
      defaultZoomLevel,
      minZoomLevel: defaultZoomLevel || 1,
      visibilityRatio: visibilityRatio || 1,
      constrainDuringPan,
      showNavigationControl,
      showNavigator,
      zoomPerClick,
      zoomPerScroll,
      animationTime,
      navPrevNextWrap,
      showRotationControl,
      minZoomImageRatio,
      maxZoomPixelRatio,
      tileSources: src,
      viewportMargins,
    });

    viewerInstance.addHandler("canvas-drag", () => setGrabbing(true));
    viewerInstance.addHandler("canvas-drag-end", () => setGrabbing(false));
    viewerInstance.addHandler("canvas-enter", () => setGrabbing(false));
    viewerInstance.addHandler("canvas-exit", () => setGrabbing(false));
    viewerInstance.addHandler("canvas-press", () => setGrabbing(true));
    viewerInstance.addHandler("canvas-release", () => setGrabbing(false));
    viewerInstance.world.addHandler("add-item", (event) => {
      const tiledImage = event.item as OpenSeadragon.TiledImage & {
        id: string;
      };
      tiledImage.id = Math.random().toString(36).substring(7);
      setTiledImage(tiledImage);
    });

    // https://github.com/openseadragon/openseadragon/issues/2376
    viewerInstance.addHandler("viewport-change", () => {
      let viewportBounds = viewerInstance.viewport.getBounds();
      let imageBounds = viewerInstance.world.getItemAt(0).getBounds(true);
      if (viewportBounds.height > imageBounds.height) {
        let target = viewerInstance.viewport.getCenter(false);
        let current = viewerInstance.viewport.getCenter(true);
        let immediate =
          target.x === current.x &&
          target.y === current.y &&
          viewerInstance.viewport.getZoom(true) ===
            viewerInstance.viewport.getZoom(false);
        let yOffset = viewportBounds.getCenter().y - imageBounds.getCenter().y;
        viewportBounds.y -= yOffset;
        viewerInstance.viewport.panTo(
          new OpenSeadragon.Point(
            current.x,
            viewportBounds.y + viewportBounds.height / 2
          ),
          true
        );
        viewerInstance.viewport.fitBounds(viewportBounds, immediate);
      }
    });

    // Listen for zoom events and update the currentZoom state
    viewerInstance.addHandler(
      "zoom",
      debounce(() => {
        setCurrentZoom(Math.round(viewerInstance.viewport.getZoom() * 10) / 10);
      }, 0)
    );

    setViewer(viewerInstance);

    return () => {
      // window.openSeadragonInit = false;
      viewerInstance.destroy();
    };
  }, [viewerRef, isMounted]);

  // Set new image
  useEffect(() => {
    if (!src || !viewer) return;
    console.log("SET NEW Image");

    setLoading(true);
    const zoomLevel = viewer.viewport.getZoom();
    const viewportCenter = viewer.viewport.getCenter();

    //viewer.world.getItemAt(0).reset();
    // const oldTiledImage = viewer.world.getItemAt(0);
    // viewer.world.removeItem(oldTiledImage);
    viewer.addTiledImage({
      tileSource: src,
      index: 0,
      preload: true,
      replace: true,
      success: function () {
        viewer.viewport.zoomTo(zoomLevel, undefined, true);
        viewer.viewport.panTo(viewportCenter, true);
        setLoading(false);
      },
    });
  }, [src]);

  // Set blurs
  useEffect(() => {
    if (
      !blurs?.length ||
      !viewer ||
      !tiledImage ||
      !viewerWrapperRef?.current ||
      lastBlursSet === JSON.stringify(blurs) // Ensure we are not duplicating blurs and old blurs are cleared
    ) {
      return;
    }
    for (const blur of blurs) {
      const element = document.createElement("div");
      element.classList.add("backdrop-blur-3xl");
      element.classList.add("bg-white/30");
      viewerWrapperRef?.current.appendChild(element);
      setLastBlursSet(JSON.stringify(blurs));
      viewer.addOverlay({
        element,
        location: tiledImage.imageToViewportRectangle(
          Number(blur.x),
          Number(blur.y),
          Number(blur.width),
          Number(blur.height)
        ),
      });
    }
  }, [blurs, viewer, tiledImage?.id]);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div ref={viewerWrapperRef} className={className}>
          {loading ? (
            <div className="fixed inset-0 m-auto h-24 w-24 rounded-lg bg-black/30 z-10 backdrop-blur-3xl flex flex-col items-center justify-center">
              <svg
                className="animate-spin h-10 w-10 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : null}
          <div
            className={`h-full w-full ${
              grabbing ? "cursor-grabbing" : "cursor-grab"
            }`}
            id="viewer"
            ref={viewerRef}
          ></div>

          <Button
            size="icon"
            className={cn(
              "hidden md:flex rounded absolute bg-transparent z-10 right-6 bottom-24 mb-2 opacity-100 transition-opacity",
              bgClassName,
              currentZoom === 1 ||
                (currentZoom === defaultZoomLevel &&
                  "opacity-0 pointer-events-none")
            )}
            variant="outline"
            onClick={resetZoom}
          >
            <HomeIcon className="h-5 w-5" />
          </Button>

          <span
            className={cn(
              "hidden md:flex bottom-4 right-6 absolute z-10 flex-col"
            )}
          >
            <Button
              size="icon"
              className={cn("rounded-b-none bg-transparent", bgClassName)}
              variant="outline"
              onClick={zoomIn}
            >
              <PlusIcon className="h-5 w-5" />
            </Button>

            <Button
              size="icon"
              onClick={zoomOut}
              className={cn(
                "rounded-t-none !border-t-transparent bg-transparent",
                bgClassName
              )}
              variant="outline"
            >
              <MinusIcon className="h-5 w-5" />
            </Button>
          </span>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onClick={() => {
            const absoluteJpgUrl = src.replace(".dzi", ".jpg");
            const link = document.createElement("a");
            link.href = absoluteJpgUrl;
            link.setAttribute("download", "");
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          className="flex items-center"
        >
          <DownloadIcon className="h-4 w-4 mr-2" />
          Download Image
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => {
            router.push(`/cams/${project}`);
            router.refresh();
          }}
        >
          Refresh
        </ContextMenuItem>
        {/* <ContextMenuItem>View Latest Image</ContextMenuItem> */}
      </ContextMenuContent>
    </ContextMenu>
  );
};
